import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import { MOCK, CURRENCIES } from "@/lib/mockData";
import { getCurrentAdmin, loginAdmin, logoutAdmin } from "@/lib/api";

const AppContext = createContext(null);

function normalizeAdmin(admin) {
  if (!admin) return null;
  const fullName = admin.fullName || admin.name || admin.email || "Admin";
  return {
    ...admin,
    avatar: admin.avatar || "",
    email: admin.email || "",
    id: admin.id || admin.adminId || admin.userId || "",
    name: fullName,
    role: admin.role || "SuperAdmin",
  };
}

export function AppProvider({ children }) {
  const [authed, setAuthed] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);

  const [currency, setCurrency] = useState(() => localStorage.getItem("ops_currency") || "NGN");

  const [shops, setShops] = useState(MOCK.shops);
  const [payments, setPayments] = useState(MOCK.payments);
  const [agents] = useState(MOCK.agents);
  const [leads, setLeads] = useState(MOCK.leads);
  const [referrals, setReferrals] = useState(MOCK.referrals);
  const [tickets, setTickets] = useState(MOCK.tickets);
  const [notifications, setNotifications] = useState(MOCK.notifications);
  const [admins, setAdmins] = useState(MOCK.admins);
  const [audit, setAudit] = useState(MOCK.audit);

  useEffect(() => {
    localStorage.setItem("ops_currency", currency);
  }, [currency]);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const payload = await getCurrentAdmin();
        if (!active) return;
        setCurrentAdmin(normalizeAdmin(payload?.admin));
        setAuthed(true);
      } catch (error) {
        if (!active) return;
        setAuthed(false);
        setCurrentAdmin(null);
      } finally {
        if (active) setAuthReady(true);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  // ---- Auth ----
  const login = useCallback(async (email, password) => {
    const payload = await loginAdmin(email, password);
    setAuthed(true);
    setCurrentAdmin(normalizeAdmin(payload?.admin));
    setAuthReady(true);
    return payload;
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutAdmin();
    } catch (error) {
      // Clear local state even if the server already expired the session.
    } finally {
      setAuthed(false);
      setCurrentAdmin(null);
    }
  }, []);

  // ---- Audit helper ----
  const logAction = useCallback((action, target = "—") => {
    const adminForLog = currentAdmin || {
      id: "system",
      name: "System",
      role: "System",
    };
    setAudit((prev) => [{
      id: `log_${Math.random().toString(36).slice(2, 9)}`,
      adminId: adminForLog.id,
      adminName: adminForLog.name,
      role: adminForLog.role,
      action,
      target,
      ip: "102.89.34.21",
      device: "Chrome / macOS",
      at: new Date().toISOString(),
      sensitive: /suspend|reset|confirm|approve|reject|delete/i.test(action),
    }, ...prev]);
  }, [currentAdmin]);

  // ---- Mutations ----
  const updateShop = useCallback((shopId, patch, actionLabel) => {
    setShops((prev) => prev.map((s) => (s.id === shopId ? { ...s, ...patch } : s)));
    const shop = shops.find((s) => s.id === shopId);
    if (actionLabel) logAction(actionLabel, shop?.name);
  }, [shops, logAction]);

  const confirmPayment = useCallback((paymentId) => {
    setPayments((prev) => prev.map((p) => (p.id === paymentId ? { ...p, status: "Manually confirmed" } : p)));
    const p = payments.find((x) => x.id === paymentId);
    logAction("confirmed payment", p?.shopName);
  }, [payments, logAction]);

  const addPayment = useCallback((shopId, amount, plan) => {
    const shop = shops.find((s) => s.id === shopId);
    if (!shop) return;
    setPayments((prev) => [{
      id: `pay_${Math.random().toString(36).slice(2, 9)}`,
      shopId,
      shopName: shop.name,
      amount,
      plan,
      method: "Manual",
      status: "Manually confirmed",
      reference: `TRX${Math.floor(Math.random() * 900000 + 100000)}`,
      createdAt: new Date().toISOString(),
    }, ...prev]);
    logAction("recorded manual payment", shop.name);
  }, [shops, logAction]);

  const updateLead = useCallback((leadId, patch) => {
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, ...patch } : l)));
  }, []);

  const updateTicket = useCallback((ticketId, patch) => {
    setTickets((prev) => prev.map((t) => (t.id === ticketId ? { ...t, ...patch } : t)));
  }, []);

  const updateReferralReward = useCallback((refId, status) => {
    setReferrals((prev) => prev.map((r) => (r.id === refId ? { ...r, rewardStatus: status } : r)));
    const r = referrals.find((x) => x.id === refId);
    logAction(`marked referral reward ${status}`, r?.shopName);
  }, [referrals, logAction]);

  const addNotification = useCallback((notif) => {
    setNotifications((prev) => [{
      id: `ntf_${Math.random().toString(36).slice(2, 9)}`,
      ...notif,
      sentAt: new Date().toISOString(),
      delivered: 0,
      opened: 0,
      failed: 0,
      status: "Queued",
    }, ...prev]);
    logAction("sent announcement", notif.audience);
  }, [logAction]);

  const addAdmin = useCallback((admin) => {
    setAdmins((prev) => [{
      id: `adm_${Math.random().toString(36).slice(2, 9)}`,
      avatar: MOCK.admins[0].avatar,
      lastLogin: null,
      status: "invited",
      twoFactor: false,
      ...admin,
    }, ...prev]);
    logAction("created admin user", admin.email);
  }, [logAction]);

  const removeAdmin = useCallback((adminId) => {
    const a = admins.find((x) => x.id === adminId);
    setAdmins((prev) => prev.filter((x) => x.id !== adminId));
    logAction("removed admin user", a?.email);
  }, [admins, logAction]);

  const value = useMemo(() => ({
    authed, authReady, login, logout, currentAdmin,
    currency, setCurrency, currencies: CURRENCIES,
    shops, updateShop,
    payments, confirmPayment, addPayment,
    agents,
    leads, updateLead,
    referrals, updateReferralReward,
    tickets, updateTicket,
    notifications, addNotification,
    admins, addAdmin, removeAdmin,
    audit, logAction,
  }), [
    authed, authReady, login, logout, currentAdmin,
    currency,
    shops, updateShop,
    payments, confirmPayment, addPayment,
    agents,
    leads, updateLead,
    referrals, updateReferralReward,
    tickets, updateTicket,
    notifications, addNotification,
    admins, addAdmin, removeAdmin,
    audit, logAction,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
